/* Northstar Axiomi — UI components & helpers */

/* ---- badges with semantic colour mapping ---- */
const BADGE_MAP = {
  // statuses
  reported: 'b-blue', triaged: 'b-blue', investigating: 'b-amber', action_pending: 'b-amber', closed: 'b-grey',
  open: 'b-blue', in_progress: 'b-amber', verification: 'b-amber', cancelled: 'b-grey',
  under_review: 'b-amber', actioned: 'b-green',
  scheduled: 'b-blue', completed: 'b-green', requested: 'b-blue', approved: 'b-green', active: 'b-green',
  submitted: 'b-blue', implemented: 'b-amber', verified: 'b-green', archived: 'b-grey',
  suspended: 'b-amber', rejected: 'b-red', expired: 'b-red', assigned: 'b-blue', advisory_issued: 'b-green',
  referred: 'b-blue', on_hold: 'b-amber', withdrawn: 'b-grey', draft: 'b-grey', review_due: 'b-amber',
  overdue: 'b-red', due_soon: 'b-amber', ok: 'b-green', current: 'b-green', review_overdue: 'b-red', assigned_: 'b-blue',
  action_assigned: 'b-amber',
  // severities / priorities / risk
  near_miss: 'b-grey', minor: 'b-blue', moderate: 'b-amber', serious: 'b-red', major: 'b-red', fatal: 'b-red',
  low: 'b-green', medium: 'b-amber', high: 'b-red', critical: 'b-red', extreme: 'b-red',
  observation: 'b-grey', routine: 'b-grey', elevated: 'b-amber',
  conform: 'b-green', minor_nc: 'b-amber', major_nc: 'b-red', na: 'b-grey', pending: 'b-grey',
  effective: 'b-green', in_service: 'b-green',
};
// Non-colour status cue so meaning survives colour-blindness / greyscale print (WCAG 1.4.1).
const BADGE_ICO = { 'b-red': '●', 'b-amber': '▲', 'b-green': '✓', 'b-blue': 'ℹ', 'b-grey': '·' };

/* ---- money: native INR, switchable to any world currency in Settings ---- */
const CURRENCY_SYMBOLS = { INR:'₹', USD:'$', EUR:'€', GBP:'£', JPY:'¥', CNY:'¥', AUD:'A$', CAD:'C$', CHF:'CHF ', SGD:'S$', HKD:'HK$', AED:'د.إ ', SAR:'﷼ ', QAR:'﷼ ', KWD:'د.ك ', BHD:'.د.ب ', OMR:'﷼ ', ZAR:'R', NGN:'₦', EGP:'£', KES:'KSh ', BRL:'R$', MXN:'$', RUB:'₽', TRY:'₺', PLN:'zł ', SEK:'kr ', NOK:'kr ', DKK:'kr ', THB:'฿', MYR:'RM ', IDR:'Rp ', PHP:'₱', VND:'₫', KRW:'₩', TWD:'NT$', BDT:'৳', LKR:'Rs ', NPR:'Rs ', PKR:'Rs ' };
function tenantCurrency() { return (State.tenant && State.tenant.currency) || 'INR'; }
function fmtMoney(n, opts = {}) {
  if (typeof opts === 'string') opts = { currency: opts }; // legacy call shape fmtMoney(v, 'INR')
  const cur = opts.currency || tenantCurrency();
  const sym = CURRENCY_SYMBOLS[cur] !== undefined ? CURRENCY_SYMBOLS[cur] : cur + ' ';
  const v = Number(n) || 0;
  const locale = cur === 'INR' ? 'en-IN' : 'en-US';
  return sym + v.toLocaleString(locale, { maximumFractionDigits: opts.decimals != null ? opts.decimals : 0 });
}

/* House mini-stat: the standard small counter card (hairline frame, tabular
 * numeral, uppercase label, tone accents). Use this — never a hand-rolled
 * centered card — so stat strips are homogeneous everywhere. */
function statMini(label, value, opts = {}) {
  const tone = opts.tone === 'bad' ? ' bad' : opts.tone === 'warn' ? ' warn' : opts.tone === 'good' ? ' good' : '';
  return `<div class="stat-mini${tone}"${opts.link ? ` data-go="${opts.link}" style="cursor:pointer"` : ''}><b>${value}</b><span>${esc(label)}</span></div>`;
}

function badge(value, label) {
  if (value == null || value === '') return '<span class="badge b-grey"><span class="b-ico" aria-hidden="true">·</span>—</span>';
  const cls = BADGE_MAP[value] || 'b-grey';
  // Never render a raw style token (e.g. 'b-grey') as a label: with no explicit label and
  // a value that is itself a badge class token, show a neutral dash instead of e.g. 'B-Grey'.
  const _txt = (label != null && label !== '') ? label : (/^b-(red|amber|green|blue|grey)$/.test(String(value)) ? '—' : titleCase(value));
  return `<span class="badge ${cls}"><span class="b-ico" aria-hidden="true">${BADGE_ICO[cls] || '·'}</span>${esc(_txt)}</span>`;
}

/* ---- toast ---- */
function toast(msg, type = 'ok') {
  const root = document.getElementById('toast-root');
  const t = document.createElement('div');
  t.className = 'toast ' + type;
  t.textContent = msg;
  root.appendChild(t);
  setTimeout(() => { t.style.opacity = '0'; setTimeout(() => t.remove(), 200); }, 2800);
}

/* ---- modal ---- */
function closeModal() { document.getElementById('modal-root').innerHTML = ''; }
/**
 * openModal({ title, fields:[{name,label,type,options,value,required,full,placeholder,hint}], submitLabel, onSubmit, size })
 * Field types: text, textarea, select, number, date, datetime-local, multitext (comma list), static
 */
function openModal(opts) {
  const fields = opts.fields || [];
  // Flatten group fields so we can both render and collect them uniformly.
  const flat = [];
  for (const f of fields) { if (f.type === 'group') flat.push(...f.fields); else flat.push(f); }
  const body = fields.map(f => fieldHtml(f)).join('');
  const root = document.getElementById('modal-root');
  const titleId = 'mdl_' + Math.random().toString(36).slice(2, 7);
  const returnFocus = document.activeElement;
  root.innerHTML = `
    <div class="modal-back" data-close-bg>
      <div class="modal ${opts.size || ''}" role="dialog" aria-modal="true" aria-labelledby="${titleId}">
        <div class="modal-h"><h3 id="${titleId}">${esc(opts.title)}</h3><button class="x" data-close aria-label="Close dialog">&times;</button></div>
        <form id="modal-form" novalidate><div class="modal-b">${body}</div>
          <div class="modal-f">
            <span class="modal-autosave muted" id="modal-autosave" aria-live="polite" style="font-size:11.5px;margin-right:auto"></span>
            <button type="button" class="btn btn-ghost" data-close>${opts.noSubmit ? 'Close' : 'Cancel'}</button>
            ${opts.noSubmit ? '' : `<button type="submit" class="btn btn-primary">${esc(opts.submitLabel || 'Save')}</button>`}
          </div>
        </form>
      </div>
    </div>`;
  // Guard against a nested openModal() called from THIS modal's own onSubmit (e.g. "create,
  // then show the result in a second modal") — the outer submit handler below always calls
  // closeAll() once opts.onSubmit resolves, but by then window.__dismissModal already points
  // at the newer, nested modal's own closeAll. Without this check, the outer close would
  // unconditionally wipe #modal-root and destroy the just-opened second modal before anyone
  // could see it (found live-verifying the "create API key -> show secret once" flow).
  const closeAll = () => {
    document.removeEventListener('keydown', onKey, true);
    if (window.__dismissModal !== closeAll) return; // a newer modal has since taken over — don't tear it down
    closeModal();
    window.__dismissModal = null;
    if (returnFocus && returnFocus.focus) { try { returnFocus.focus(); } catch (_) {} }
  };
  window.__dismissModal = closeAll;
  root.querySelectorAll('[data-close]').forEach(b => b.onclick = closeAll);
  if (typeof translateDOM === 'function') translateDOM(root);
  if (typeof window.iconizeText === 'function') window.iconizeText(root);
  root.querySelector('[data-close-bg]').onclick = (e) => { if (e.target.matches('[data-close-bg]')) closeAll(); };
  // focus trap + escape + autofocus first field
  const modalEl = root.querySelector('.modal');
  const focusables = () => [...modalEl.querySelectorAll('input,select,textarea,button,[href],[tabindex]:not([tabindex="-1"])')].filter((el) => !el.disabled && el.offsetParent !== null);
  const onKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); closeAll(); return; }
    if (e.key !== 'Tab') return;
    const f = focusables(); if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  document.addEventListener('keydown', onKey, true);
  setTimeout(() => { const f = focusables().filter((el) => !el.matches('[data-close]') && el.type !== 'submit'); (f[0] || focusables()[0] || {}).focus && (f[0] || focusables()[0]).focus(); }, 20);

  // ---- value collect / restore (shared by autosave + submit) ----
  const collect = () => {
    const d = {};
    for (const f of flat) {
      if (f.type === 'static') continue;
      if (f.type === 'checklist') { d[f.name] = Array.from(root.querySelectorAll(`[name="${f.name}"]:checked`)).map((el) => el.value); continue; }
      const elx = root.querySelector(`[name="${f.name}"]`);
      if (!elx) continue;
      let v = elx.value;
      if (f.type === 'checkbox') v = elx.checked;
      else if (f.type === 'number') v = v === '' ? null : Number(v);
      else if (f.type === 'multitext') v = v.split(',').map(s => s.trim()).filter(Boolean);
      d[f.name] = v;
    }
    return d;
  };
  const setVals = (d) => {
    for (const f of flat) {
      if (f.type === 'static' || !(f.name in d)) continue;
      if (f.type === 'checklist') { const set = new Set((d[f.name] || []).map(String)); root.querySelectorAll(`[name="${f.name}"]`).forEach((el) => { el.checked = set.has(el.value); }); continue; }
      const elx = root.querySelector(`[name="${f.name}"]`); if (!elx) continue;
      if (f.type === 'checkbox') elx.checked = !!d[f.name];
      else if (f.type === 'multitext') elx.value = Array.isArray(d[f.name]) ? d[f.name].join(', ') : (d[f.name] || '');
      else elx.value = d[f.name] == null ? '' : d[f.name];
      if (f.type === 'lines') { const ed = elx.closest('[data-lines]'); if (ed && ed.__paint) ed.__paint(); }
    }
  };

  // ---- 'lines' chip editors: hidden input holds the \n-joined truth ----
  initLinesEditors(root);

  // ---- drag-drop file fields (type:'file') ----
  root.querySelectorAll('[data-dz]').forEach((dz) => {
    const name = dz.getAttribute('data-dz');
    const hidden = dz.querySelector('input[type=hidden]');
    const prev = dz.querySelector('.dz-preview');
    const accept = dz.getAttribute('data-accept') || '';
    const setFile = (o) => { if (!o) return; hidden.value = o.dataUrl; prev.innerHTML = /^data:image\//.test(o.dataUrl) ? `<img src="${o.dataUrl}" style="max-height:70px;border-radius:6px">` : `<span class="badge b-green">📎 ${esc(o.name || 'file')}</span>`; hidden.dispatchEvent(new Event('input', { bubbles: true })); };
    if (typeof UI !== 'undefined') UI.dropZone(dz, setFile, accept);
    const pick = dz.querySelector('[data-dz-pick]'); if (pick) pick.onclick = async () => { const o = await pickFileDataUrl(accept || 'image/*', 4 * 1024 * 1024); if (o) setFile(o); };
  });

  // ---- autosave draft (skip sensitive forms) ----
  const slug = String(opts.title || 'form').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 48);
  const draftKey = 'esos_draft_' + slug;
  const sensitive = opts.noDraft || flat.some((f) => f.type === 'password') || /password|sign in|two-factor|two factor|\bmfa\b|recovery/i.test(opts.title || '');
  const hasContent = (d) => Object.values(d).some((v) => v !== '' && v != null && v !== false && !(Array.isArray(v) && !v.length));
  const clearDraft = () => { try { localStorage.removeItem(draftKey); } catch (_) {} };
  if (!sensitive) {
    let draft = null; try { draft = JSON.parse(localStorage.getItem(draftKey) || 'null'); } catch (_) {}
    if (draft && draft.data && hasContent(draft.data)) {
      const b = document.createElement('div'); b.className = 'draft-banner';
      b.innerHTML = `<span>💾 Unsaved draft from ${esc(new Date(draft.t).toLocaleString())}</span><span style="white-space:nowrap"><button type="button" class="btn btn-sm btn-primary" data-dr>Restore</button> <button type="button" class="btn btn-sm btn-ghost" data-dd>Discard</button></span>`;
      const mb = root.querySelector('.modal-b'); mb.insertBefore(b, mb.firstChild);
      b.querySelector('[data-dr]').onclick = () => { setVals(draft.data); b.remove(); };
      b.querySelector('[data-dd]').onclick = () => { clearDraft(); b.remove(); };
    }
    const asEl = root.querySelector('#modal-autosave');
    const setAs = (t) => { if (asEl) asEl.textContent = t; };
    let st; root.querySelector('#modal-form').addEventListener('input', () => { clearTimeout(st); setAs('Saving…'); st = setTimeout(() => { const d = collect(); if (hasContent(d)) { try { localStorage.setItem(draftKey, JSON.stringify({ t: Date.now(), data: d })); setAs('✓ Draft saved'); } catch (_) { setAs(''); } } else setAs(''); }, 400); });
  }

  // ---- inline validation ----
  const clearErr = (nm) => { const e = root.querySelector(`.field-err[data-err-for="${nm}"]`); if (e) e.remove(); const el = root.querySelector(`[name="${nm}"]`); if (el) el.classList.remove('inp-err'); };
  root.querySelector('#modal-form').addEventListener('input', (e) => { if (e.target && e.target.name) clearErr(e.target.name); });
  const showErr = (nm, msg) => { const el = root.querySelector(`[name="${nm}"]`); if (!el) return; el.classList.add('inp-err'); const row = el.closest('.form-row') || el.parentElement; if (row.querySelector(`.field-err[data-err-for="${nm}"]`)) { row.querySelector(`.field-err[data-err-for="${nm}"]`).textContent = msg; return; } const e = document.createElement('div'); e.className = 'field-err'; e.setAttribute('data-err-for', nm); e.textContent = msg; row.appendChild(e); };
  const validate = (data) => {
    let firstBad = null;
    for (const f of flat) {
      if (f.type === 'static') continue;
      const v = data[f.name]; let msg = '';
      if (f.required && (v === '' || v == null || (Array.isArray(v) && !v.length) || (f.type === 'checkbox' && !v))) msg = 'Required';
      else if (!msg && f.type === 'email' && v && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) msg = 'Enter a valid email address';
      else if (!msg && f.type === 'number' && v != null) { if (f.min != null && v < f.min) msg = 'Must be ≥ ' + f.min; else if (f.max != null && v > f.max) msg = 'Must be ≤ ' + f.max; }
      else if (!msg && typeof f.validate === 'function') { const r = f.validate(v, data); if (r) msg = r; }
      if (msg) { showErr(f.name, msg); if (!firstBad) firstBad = f.name; }
    }
    if (firstBad) { const el = root.querySelector(`[name="${firstBad}"]`); if (el) el.focus(); }
    return !firstBad;
  };

  if (typeof opts.onRender === 'function') { try { opts.onRender(root); } catch (_) {} }
  root.querySelector('#modal-form').onsubmit = async (e) => {
    e.preventDefault();
    const data = collect();
    if (!validate(data)) return;
    const btn = root.querySelector('button[type=submit]'); btn.disabled = true; btn.textContent = 'Saving…';
    try { await opts.onSubmit(data); clearDraft(); closeAll(); }
    catch (err) {
      btn.disabled = false; btn.textContent = opts.submitLabel || 'Save';
      // A structured validation error from the server (core/validate.js: {error, field, min, max,
      // reason}) is pinned to its field so the person sees the limit where they typed.
      const d = err && err.data; const fld = d && d.field && flat.find((f) => f.name === d.field);
      if (fld) { showErr(fld.name, err.message); const el = root.querySelector(`[name="${fld.name}"]`); if (el && el.focus) el.focus(); }
      toast(err.message || 'Error', 'err');
    }
  };
}
/* 'lines' chip-editor wiring — shared by openModal fields and hand-built forms
 * (e.g. the PTW request). The hidden input holds the \n-joined truth, so any
 * capture code reading .value keeps working unchanged. */
function initLinesEditors(scope) {
  (scope || document).querySelectorAll('[data-lines]').forEach((ed) => {
    const hidden = ed.querySelector('input[type=hidden]');
    const list = ed.querySelector('.lines-list');
    const addIn = ed.querySelector('.lines-add input');
    const addBtn = ed.querySelector('.lines-add button');
    if (!hidden || !list || !addIn) return;
    const lines = () => String(hidden.value || '').split('\n').map((s) => s.trim()).filter(Boolean);
    const ping = () => hidden.dispatchEvent(new Event('input', { bubbles: true }));
    const paint = () => {
      list.innerHTML = lines().map((l, i) => `<div class="line-chip"><span>${esc(l)}</span><button type="button" data-rm="${i}" aria-label="Remove line">✕</button></div>`).join('');
      list.querySelectorAll('[data-rm]').forEach((b) => b.onclick = () => { const ls = lines(); ls.splice(Number(b.dataset.rm), 1); hidden.value = ls.join('\n'); ping(); paint(); });
    };
    const add = () => { const v = addIn.value.trim(); if (!v) return; hidden.value = lines().concat(v).join('\n'); addIn.value = ''; ping(); paint(); addIn.focus(); };
    if (addBtn) addBtn.onclick = add;
    addIn.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } });
    // pasting a multi-line block splits into chips — migration-friendly
    addIn.addEventListener('paste', (e) => { const txt = (e.clipboardData || {}).getData ? e.clipboardData.getData('text') : ''; if (txt && txt.includes('\n')) { e.preventDefault(); hidden.value = lines().concat(txt.split('\n').map((s) => s.trim()).filter(Boolean)).join('\n'); ping(); paint(); } });
    ed.__paint = paint; paint();
  });
}

function fieldHtml(f) {
  if (f.type === 'group') return `<div class="form-grid" style="grid-template-columns:repeat(${Math.min(Math.max(f.fields.length, 1), 4)},minmax(0,1fr))">${f.fields.map(fieldHtml).join('')}</div>`;
  if (f.type === 'static') return `<div class="form-row"><label>${esc(f.label)}</label><div class="val">${f.value || ''}</div></div>`;
  const helpDot = (f.help && typeof infoDot === 'function') ? ' ' + infoDot(f.help) : '';
  const wrap = (inner) => `<div class="form-row" style="${f.full === false ? '' : ''}">${f.label ? `<label>${esc(f.label)}${f.required ? ' *' : ''}${helpDot}</label>` : ''}${inner}${f.hint ? `<div class="muted" style="font-size:11px;margin-top:3px;color:var(--muted)">${esc(f.hint)}</div>` : ''}</div>`;
  const req = f.required ? 'required' : '';
  if (f.type === 'checkbox') return `<div class="form-row"><label style="font-weight:400;display:flex;align-items:center;gap:8px;cursor:pointer"><input name="${f.name}" type="checkbox" ${f.value ? 'checked' : ''} style="width:auto"> ${esc(f.label || '')}${f.required ? ' *' : ''}</label>${f.hint ? `<div class="muted" style="font-size:11px;margin-top:3px">${esc(f.hint)}</div>` : ''}</div>`;
  if (f.type === 'file') return wrap(`<div class="dz" data-dz="${esc(f.name)}" ${f.accept ? `data-accept="${esc(f.accept)}"` : ''}><input type="hidden" name="${f.name}" value="${esc(f.value || '')}"><div class="dz-inner">📎 Drag a file here, or <button type="button" class="btn btn-ghost btn-sm" data-dz-pick>Choose file</button></div><div class="dz-preview">${f.value && /^data:image\//.test(f.value) ? `<img src="${f.value}" style="max-height:70px;border-radius:6px">` : ''}</div></div>`);
  if (f.type === 'textarea') return wrap(`<textarea name="${f.name}" rows="${f.rows || 3}" placeholder="${esc(f.placeholder || '')}" ${req}>${esc(f.value || '')}</textarea>`);
  // 'lines' — a curated line editor: each entry is a removable row-chip with an
  // add box, serialised to the same newline-joined string a textarea produced,
  // so no server change is needed. The precision answer to free-text walls.
  if (f.type === 'lines') return wrap(`<div class="lines-ed" data-lines="${esc(f.name)}"><input type="hidden" name="${f.name}" value="${esc(f.value || '')}"><div class="lines-list"></div><div class="lines-add"><input type="text" placeholder="${esc(f.placeholder || 'Add a line and press Enter')}"><button type="button" class="btn btn-ghost btn-sm">＋ ${typeof t === 'function' ? t('Add') : 'Add'}</button></div></div>`);
  if (f.type === 'checklist') { const sel = new Set((Array.isArray(f.value) ? f.value : []).map(String)); return wrap(`<div class="checklist">${(f.options || []).map(o => { const val = o.value !== undefined ? o.value : o; const lab = o.label !== undefined ? o.label : titleCase(o); return `<label class="chk-item"><input type="checkbox" name="${f.name}" value="${esc(val)}" ${sel.has(String(val)) ? 'checked' : ''} style="width:auto"> ${esc(lab)}</label>`; }).join('')}</div>`); }
  if (f.type === 'select') return wrap(`<select name="${f.name}" ${req}>${f.optionsHtml || (f.options || []).map(o => { const val = o.value !== undefined ? o.value : o; const lab = o.label !== undefined ? o.label : titleCase(o); return `<option value="${esc(val)}" ${String(f.value) === String(val) ? 'selected' : ''}>${esc(lab)}</option>`; }).join('')}</select>`);
  const type = f.type === 'multitext' ? 'text' : (f.type || 'text');
  // Hardening F (26 Sep): numeric limits declared on the field spec reach the DOM, so the
  // spinner, the client-side validate() and the server's numIn() enforce the same range.
  const lim = f.type === 'number' ? `${f.min != null ? ` min="${f.min}"` : ''}${f.max != null ? ` max="${f.max}"` : ''}${f.step != null ? ` step="${f.step}"` : ''}${f.integer ? ' step="1"' : ''}` : '';
  return wrap(`<input name="${f.name}" type="${type}" value="${esc(f.value != null ? f.value : '')}" placeholder="${esc(f.placeholder || (f.type === 'multitext' ? 'comma,separated' : ''))}"${lim} ${req}>`);
}

/* ---- groupable form fields side-by-side ---- */
function formGroup(...fieldHtmls) { return `<div class="form-grid">${fieldHtmls.join('')}</div>`; }

/* ---- AI-360 W1-C: shared "draft with AI" runner --------------------------------
 * Calls a draft-only endpoint (nothing persists server-side) and hands the result
 * to `apply`. The button shows a busy state; errors surface as toasts. Honesty
 * rule: the server says how the draft was made — `aiMethodChip` renders that so a
 * deterministic fallback is never mistaken for an AI judgement. */
async function aiDraft(btn, path, body, apply) {
  const tt = (s) => (typeof t === 'function' ? t(s) : s);
  const old = btn.innerHTML; btn.disabled = true; btn.innerHTML = '⋯ ' + tt('drafting');
  try { const r = await api.post(path, body || {}); apply(r); }
  catch (e) { toast((e && e.message) || tt('Draft failed'), 'err'); }
  finally { btn.disabled = false; btn.innerHTML = old; }
}
function aiMethodChip(method) {
  const tt = (s) => (typeof t === 'function' ? t(s) : s);
  const ai = ['ai_drafted', 'ai_constrained', 'ai_extracted'].includes(method);
  return `<span class="badge ${ai ? 'b-blue' : 'b-grey'}" style="font-size:10px">${ai ? '🤖 ' + tt('AI draft — review before use') : tt('deterministic draft — no AI configured')}</span>`;
}

/* ---- deep-link filters: read ?key=value from the hash and show a clearable chip ----
 * applyHashFilter(rows, baseHash, specs) → { rows, bar }
 *   specs: [{ param, label, test(row, value) }]  (test omitted → row[param] === value) */
function hashParams() { return new URLSearchParams((location.hash.split('?')[1] || '')); }
function applyHashFilter(rows, baseHash, specs) {
  const p = hashParams(); const chips = [];
  for (const s of specs) {
    const v = p.get(s.param); if (v == null || v === '') continue;
    rows = rows.filter(r => s.test ? s.test(r, v) : String(r[s.param]) === v);
    chips.push(s.label ? (typeof s.label === 'function' ? s.label(v) : s.label) : `${s.param}: ${v}`);
  }
  const bar = chips.length
    ? `<div class="toolbar" style="margin-bottom:12px;align-items:center"><span class="muted" style="font-size:12px">Filtered:</span>${chips.map(c => `<span class="badge b-blue">${esc(c)}</span>`).join('')}<a data-go="${esc(baseHash)}" class="btn btn-ghost btn-sm" style="margin-left:4px">✕ Clear filter</a></div>`
    : '';
  return { rows, bar };
}

/* ---- data-grid (shared) ----
 * table(cols, rows, rowClick, opts)
 *   cols: { key, label, width, render(r), sortable(false to disable), sortVal(r), csv(r) }
 *   opts: { page=25, search=true, columns=true, exportName, viewsKey, bulk:[{label,icon,run(selectedRows)}], empty:{icon,title,sub,cta,ctaGo,ctaAct} }
 * Every list gains: sortable headers, search, pagination, column chooser, CSV export,
 * saved views, keyboard-operable rows, and (optional) bulk actions — with no view changes. */
const _GRID = {};
function _cellText(c, r) {
  if (c.csv) return String(c.csv(r) == null ? '' : c.csv(r));
  if (c.sortVal) { const v = c.sortVal(r); if (v != null && typeof v !== 'object') return String(v); }
  if (c.key != null && r[c.key] != null && typeof r[c.key] !== 'object') return String(r[c.key]);
  if (c.render) return String(c.render(r) || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return '';
}
function table(cols, rows, rowClick, opts) {
  opts = opts || {};
  cols = cols.filter(Boolean);
  const id = 'g_' + Math.random().toString(36).slice(2, 9);
  const viewsKey = opts.viewsKey || (typeof location !== 'undefined' ? location.hash.replace(/^#\/?/, '').split('?')[0] : id);
  const g = _GRID[id] = {
    cols, rows, rowClick, opts, viewsKey,
    q: '', sort: -1, dir: 1, page: 0, size: opts.page || 25,
    sel: new Set(), showChooser: false,
    show: new Set(cols.map((c, i) => i)),
  };
  if (!rows.length) {
    if (opts.empty) return UI.emptyState(opts.empty);
    // Universal guided empty state — never a dead "No records yet". Derive a readable
    // register name from the route so the message is always specific.
    const label = (opts.emptyLabel || String(viewsKey).replace(/[-_/]+/g, ' ').trim() || 'records');
    return `<div class="card card-pad">${UI.emptyState({ icon: opts.emptyIcon || '📭', title: `No ${label} yet`, sub: opts.emptyHint || 'Nothing here yet — add your first record with the button above, or import an existing register.' })}</div>`;
  }
  // First paint is synchronous (so views can query cells right away); bind + later
  // re-renders happen in place.
  setTimeout(() => _bindGrid(id));
  return `<div class="card" id="${id}">${_gridHTML(id)}</div>`;
}
// Inner markup for the grid (toolbar + table + pager + bulk bar).
function _gridHTML(id) {
  const g = _GRID[id]; if (!g) return '';
  const o = g.opts;
  const all = _gridFiltered(g);
  const total = all.length, pages = Math.max(1, Math.ceil(total / g.size));
  if (g.page >= pages) g.page = pages - 1;
  const pageRows = all.slice(g.page * g.size, g.page * g.size + g.size);
  const hasBulk = Array.isArray(o.bulk) && o.bulk.length;
  const searchOn = o.search !== false && g.rows.length > 8;
  const showToolbar = searchOn || o.columns !== false || o.exportName !== false || o.viewsKey !== false;
  let toolbar = '';
  if (showToolbar) {
    toolbar = `<div class="grid-toolbar">
      ${searchOn ? `<div class="gt-search"><span class="si">⌕</span><input id="${id}_q" value="${esc(g.q)}" placeholder="Filter ${total} record${total === 1 ? '' : 's'}…" aria-label="Filter table"></div>` : `<span class="gt-count">${total} record${total === 1 ? '' : 's'}</span>`}
      <span class="spacer"></span>
      ${o.viewsKey !== false ? `<button class="gt-btn" id="${id}_views" title="Saved views">☰ Views</button>` : ''}
      ${o.columns !== false ? `<button class="gt-btn" id="${id}_cols" title="Choose columns">⧉ Columns</button>` : ''}
      ${o.exportName !== false ? `<button class="gt-btn" id="${id}_exp" title="Export this view to CSV">⬇ CSV</button>` : ''}
    </div>`;
  }
  const head = `<tr>
    ${hasBulk ? `<th class="cbx"><input type="checkbox" id="${id}_allck" aria-label="Select all"></th>` : ''}
    ${g.cols.map((c, i) => {
      if (!g.show.has(i)) return '';
      const sortable = c.sortable !== false && (c.key != null || c.sortVal || c.csv || c.render);
      const sorted = g.sort === i;
      return `<th class="${sortable ? 'sortable' : ''} ${sorted ? 'sorted' : ''}" data-col="${i}" style="${c.width ? 'width:' + c.width : ''}">${esc(c.label)}${sortable ? `<span class="s-caret">${sorted ? (g.dir > 0 ? '▲' : '▼') : '↕'}</span>` : ''}</th>`;
    }).join('')}
  </tr>`;
  const body = pageRows.map(({ r, i }) => {
    const seld = g.sel.has(i);
    return `<tr data-row="${i}" ${g.rowClick ? 'tabindex="0" role="button"' : ''} class="${seld ? 'sel' : ''}">
      ${hasBulk ? `<td class="cbx"><input type="checkbox" class="${id}_ck" data-i="${i}" ${seld ? 'checked' : ''} aria-label="Select row"></td>` : ''}
      ${g.cols.map((c, ci) => g.show.has(ci) ? `<td>${c.render ? c.render(r) : esc(r[c.key] == null ? '' : r[c.key])}</td>` : '').join('')}
    </tr>`;
  }).join('');
  const bulkbar = (hasBulk && g.sel.size) ? `<div class="grid-bulkbar"><b>${g.sel.size} selected</b>${o.bulk.map((b, bi) => `<button class="btn btn-sm" data-bulk="${bi}">${b.icon || ''} ${esc(b.label)}</button>`).join('')}<button class="btn btn-sm" id="${id}_expsel">⬇ Export selected</button><span class="spacer" style="flex:1"></span><button class="btn btn-sm" id="${id}_clr">Clear</button></div>` : '';
  const pager = pages > 1 ? `<div class="grid-pager"><span>Page ${g.page + 1} of ${pages} · showing ${pageRows.length} of ${total}</span><div class="pg-btns"><button id="${id}_prev" ${g.page === 0 ? 'disabled' : ''}>‹ Prev</button><button id="${id}_next" ${g.page >= pages - 1 ? 'disabled' : ''}>Next ›</button></div></div>` : '';
  return `${bulkbar}${toolbar}<div style="overflow-x:auto"><table class="tbl"><thead>${head}</thead><tbody>${body}</tbody></table></div>${pager}`;
}
function _renderGrid(id) { const host = document.getElementById(id); if (!host) return; host.className = 'card'; host.innerHTML = _gridHTML(id); _bindGrid(id); }
function _gridFiltered(g) {
  let rs = g.rows.map((r, i) => ({ r, i }));
  if (g.q) { const ql = g.q.toLowerCase(); rs = rs.filter(({ r }) => g.cols.some((c) => _cellText(c, r).toLowerCase().includes(ql))); }
  if (g.sort >= 0) {
    const c = g.cols[g.sort];
    rs.sort((a, b) => {
      const va = c.sortVal ? c.sortVal(a.r) : _cellText(c, a.r), vb = c.sortVal ? c.sortVal(b.r) : _cellText(c, b.r);
      const na = Number(va), nb = Number(vb);
      let cmp;
      if (!isNaN(na) && !isNaN(nb) && va !== '' && vb !== '') cmp = na - nb;
      else cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
      return cmp * g.dir;
    });
  }
  return rs;
}
function _bindGrid(id) {
  const g = _GRID[id]; if (!g) return;
  const host = document.getElementById(id); if (!host) return;
  const o = g.opts;
  const all = _gridFiltered(g);
  const pages = Math.max(1, Math.ceil(all.length / g.size));
  const pageRows = all.slice(g.page * g.size, g.page * g.size + g.size);
  const hasBulk = Array.isArray(o.bulk) && o.bulk.length;
  const searchOn = o.search !== false && g.rows.length > 8;
  const rerender = () => _renderGrid(id);
  host.querySelectorAll('th[data-col]').forEach((th) => { if (!th.classList.contains('sortable')) return; th.onclick = () => { const ci = Number(th.dataset.col); if (g.sort === ci) g.dir = -g.dir; else { g.sort = ci; g.dir = 1; } rerender(); }; });
  if (searchOn) { const qi = document.getElementById(id + '_q'); if (qi) { qi.oninput = () => { g.q = qi.value; g.page = 0; rerender(); const n = document.getElementById(id + '_q'); if (n) { n.focus(); n.setSelectionRange(n.value.length, n.value.length); } }; } }
  const pv = document.getElementById(id + '_prev'); if (pv) pv.onclick = () => { g.page = Math.max(0, g.page - 1); rerender(); };
  const nx = document.getElementById(id + '_next'); if (nx) nx.onclick = () => { g.page++; rerender(); };
  // rows
  host.querySelectorAll('tbody tr[data-row]').forEach((tr) => {
    const idx = Number(tr.dataset.row);
    if (g.rowClick) {
      const act = (e) => { if (e.target.closest('input,button,a,[data-go]')) return; g.rowClick(g.rows[idx]); };
      tr.onclick = act;
      tr.onkeydown = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); g.rowClick(g.rows[idx]); } };
    }
  });
  // bulk select
  if (hasBulk) {
    host.querySelectorAll('.' + id + '_ck').forEach((ck) => ck.onchange = () => { const i = Number(ck.dataset.i); ck.checked ? g.sel.add(i) : g.sel.delete(i); rerender(); });
    const allck = document.getElementById(id + '_allck'); if (allck) { allck.checked = pageRows.every(({ i }) => g.sel.has(i)) && pageRows.length > 0; allck.onchange = () => { pageRows.forEach(({ i }) => allck.checked ? g.sel.add(i) : g.sel.delete(i)); rerender(); }; }
    const clr = document.getElementById(id + '_clr'); if (clr) clr.onclick = () => { g.sel.clear(); rerender(); };
    const es = document.getElementById(id + '_expsel'); if (es) es.onclick = () => _gridExport(g, [...g.sel].map((i) => ({ r: g.rows[i] })), (o.exportName || g.viewsKey || 'export') + '-selected');
    host.querySelectorAll('[data-bulk]').forEach((b) => b.onclick = async () => { const rows = [...g.sel].map((i) => g.rows[i]); try { await o.bulk[Number(b.dataset.bulk)].run(rows); } catch (e) { toast(e.message || 'Action failed', 'err'); } });
  }
  // export
  const exp = document.getElementById(id + '_exp'); if (exp) exp.onclick = () => _gridExport(g, all, o.exportName || g.viewsKey || 'export');
  // column chooser
  const colBtn = document.getElementById(id + '_cols'); if (colBtn) colBtn.onclick = (e) => { e.stopPropagation(); _gridColChooser(id, colBtn); };
  // saved views
  const vBtn = document.getElementById(id + '_views'); if (vBtn) vBtn.onclick = (e) => { e.stopPropagation(); _gridViews(id, vBtn); };
  if (typeof bindGo === 'function') bindGo();
  if (typeof bindPhotoLightbox === 'function') bindPhotoLightbox();
  if (typeof translateDOM === 'function') translateDOM(host);
}
function _gridExport(g, rows, name) {
  const visCols = g.cols.filter((c, i) => g.show.has(i));
  const cell = (v) => { const s = String(v == null ? '' : v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
  const lines = [visCols.map((c) => cell(c.label)).join(',')];
  for (const { r } of rows) lines.push(visCols.map((c) => cell(_cellText(c, r))).join(','));
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = String(name).replace(/[^a-z0-9_-]+/gi, '-') + '.csv'; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  toast('Exported ' + rows.length + ' rows');
}
function _gridColChooser(id, anchor) {
  const g = _GRID[id]; if (!g) return;
  document.querySelectorAll('.col-chooser').forEach((e) => e.remove());
  const box = document.createElement('div'); box.className = 'col-chooser';
  box.innerHTML = g.cols.map((c, i) => `<label><input type="checkbox" data-ci="${i}" ${g.show.has(i) ? 'checked' : ''}> ${esc(c.label)}</label>`).join('');
  anchor.parentElement.style.position = 'relative'; anchor.parentElement.appendChild(box);
  box.querySelectorAll('input').forEach((ck) => ck.onchange = () => { const i = Number(ck.dataset.ci); if (ck.checked) g.show.add(i); else if (g.show.size > 1) g.show.delete(i); else ck.checked = true; });
  const off = (e) => { if (!box.contains(e.target) && e.target !== anchor) { box.remove(); document.removeEventListener('click', off); _renderGrid(id); } };
  setTimeout(() => document.addEventListener('click', off), 0);
}
function _gridViews(id, anchor) {
  const g = _GRID[id]; if (!g) return;
  const saved = UI.SavedViews.all(g.viewsKey);
  const rows = saved.map((v) => `<label style="justify-content:space-between"><span data-load="${esc(v.name)}" style="cursor:pointer;flex:1">▸ ${esc(v.name)}</span><span data-del="${esc(v.name)}" style="cursor:pointer;color:var(--red)">✕</span></label>`).join('') || '<div class="muted" style="padding:6px 8px;font-size:12px">No saved views yet.</div>';
  document.querySelectorAll('.col-chooser').forEach((e) => e.remove());
  const box = document.createElement('div'); box.className = 'col-chooser'; box.style.minWidth = '220px';
  box.innerHTML = `<div class="muted" style="font-size:10.5px;text-transform:uppercase;letter-spacing:.5px;font-weight:700;padding:4px 8px">Saved views</div>${rows}<div style="border-top:1px solid var(--line);margin-top:6px;padding-top:6px"><button class="gt-btn" data-save style="width:100%;justify-content:center">＋ Save current view</button></div>`;
  anchor.parentElement.style.position = 'relative'; anchor.parentElement.appendChild(box);
  box.querySelector('[data-save]').onclick = () => { const name = prompt('Name this view (filter, sort & columns):'); if (!name) return; UI.SavedViews.save(g.viewsKey, name, { q: g.q, sort: g.sort, dir: g.dir, size: g.size, show: [...g.show] }); toast('View saved'); box.remove(); };
  box.querySelectorAll('[data-load]').forEach((el) => el.onclick = () => { const v = saved.find((x) => x.name === el.dataset.load); if (v) { g.q = v.state.q || ''; g.sort = v.state.sort; g.dir = v.state.dir || 1; g.size = v.state.size || g.size; if (v.state.show) g.show = new Set(v.state.show); g.page = 0; } box.remove(); _renderGrid(id); });
  box.querySelectorAll('[data-del]').forEach((el) => el.onclick = (e) => { e.stopPropagation(); UI.SavedViews.remove(g.viewsKey, el.dataset.del); box.remove(); _gridViews(id, anchor); });
  const off = (e) => { if (!box.contains(e.target) && e.target !== anchor) { box.remove(); document.removeEventListener('click', off); } };
  setTimeout(() => document.addEventListener('click', off), 0);
}

/* ---- QR scan (employee badge) — uses native BarcodeDetector; falls back to manual ---- */
async function scanQR() {
  if (!('BarcodeDetector' in window) || !navigator.mediaDevices) { const v = prompt('Enter employee code (QR scanning not supported on this browser):'); return v ? v.trim() : null; }
  let stream;
  try { stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } }); }
  catch (e) { const v = prompt('Camera unavailable — enter employee code:'); return v ? v.trim() : null; }
  return new Promise((resolve) => {
    const root = document.getElementById('modal-root');
    root.innerHTML = `<div class="modal-back" data-close-bg style="display:flex;align-items:center;justify-content:center"><div style="position:relative;text-align:center"><video id="qr-video" autoplay playsinline muted style="width:90vw;max-width:420px;border-radius:14px;background:#000"></video><div style="position:absolute;left:10%;right:10%;top:20%;bottom:20%;border:3px solid #0E7C66;border-radius:12px;pointer-events:none"></div><div style="color:#fff;margin-top:10px;font-size:13px">Point the camera at the employee QR badge</div></div></div>`;
    const v = document.getElementById('qr-video'); v.srcObject = stream;
    let done = false;
    const stop = () => { done = true; try { stream.getTracks().forEach((t) => t.stop()); } catch (_) {} root.innerHTML = ''; };
    root.querySelector('[data-close-bg]').onclick = (e) => { if (e.target.matches('[data-close-bg]')) { stop(); resolve(null); } };
    const det = new BarcodeDetector({ formats: ['qr_code'] });
    const tick = async () => {
      if (done) return;
      try { const codes = await det.detect(v); if (codes && codes.length && codes[0].rawValue) { const val = codes[0].rawValue.trim(); stop(); resolve(val); return; } } catch (_) {}
      requestAnimationFrame(tick);
    };
    v.onloadedmetadata = () => v.play().then(tick).catch(tick);
  });
}

/* ---- geolocation stamp (non-blocking; resolves null if denied/unavailable) ---- */
function getGeo(timeoutMs = 6000) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60000 },
    );
  });
}
function geoStr(g) {
  if (!g || typeof g.lat !== 'number') return '';
  return `📍 ${g.lat.toFixed(4)}, ${g.lng.toFixed(4)}${g.acc ? ` ±${Math.round(g.acc)} m` : ''}`;
}

/* ---- photo capture / attach (live camera on mobile, file picker on desktop) ---- */
function pickImage() {
  return new Promise((resolve) => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'image/*'; inp.setAttribute('capture', 'environment'); inp.style.display = 'none';
    inp.onchange = () => {
      const file = inp.files && inp.files[0];
      if (!file) { resolve(null); inp.remove(); return; }
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const max = 1280; let w = img.width, h = img.height;
          if (w > max || h > max) { const s = Math.min(max / w, max / h); w = Math.round(w * s); h = Math.round(h * s); }
          const c = document.createElement('canvas'); c.width = w; c.height = h;
          c.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(c.toDataURL('image/jpeg', 0.7)); inp.remove();
        };
        img.onerror = () => { resolve(null); inp.remove(); };
        img.src = reader.result;
      };
      reader.onerror = () => { resolve(null); inp.remove(); };
      reader.readAsDataURL(file);
    };
    document.body.appendChild(inp); inp.click();
  });
}
/* ---- short video capture / attach (returns a data URL; capped ~8 MB) ---- */
function pickVideo(maxBytes = 8 * 1024 * 1024) {
  return new Promise((resolve) => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'video/*'; inp.setAttribute('capture', 'environment'); inp.style.display = 'none';
    inp.onchange = () => {
      const file = inp.files && inp.files[0];
      if (!file) { resolve(null); inp.remove(); return; }
      if (file.size > maxBytes) { toast('Video too large — keep it under 8 MB (a few seconds).', 'err'); resolve(null); inp.remove(); return; }
      const reader = new FileReader();
      reader.onload = () => { resolve(reader.result); inp.remove(); };
      reader.onerror = () => { resolve(null); inp.remove(); };
      reader.readAsDataURL(file);
    };
    document.body.appendChild(inp); inp.click();
  });
}
/* ---- raw file → data URL (no re-encoding; preserves PNG alpha & SVG) ---- */
function pickFileDataUrl(accept = 'image/*', maxBytes = 512 * 1024) {
  return new Promise((resolve) => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = accept; inp.style.display = 'none';
    inp.onchange = () => {
      const file = inp.files && inp.files[0];
      if (!file) { resolve(null); inp.remove(); return; }
      if (file.size > maxBytes) { toast(`File too large — keep it under ${Math.round(maxBytes / 1024)} KB.`, 'err'); resolve(null); inp.remove(); return; }
      const reader = new FileReader();
      reader.onload = () => { resolve({ dataUrl: reader.result, name: file.name, bytes: file.size }); inp.remove(); };
      reader.onerror = () => { resolve(null); inp.remove(); };
      reader.readAsDataURL(file);
    };
    document.body.appendChild(inp); inp.click();
  });
}
async function attachPhoto(endpoint, after) {
  const dataUrl = await pickImage();
  if (!dataUrl) return;
  try { await api.post(endpoint, { dataUrl }); toast('Photo added'); if (after) after(); }
  catch (e) { toast(e.message || 'Upload failed', 'err'); }
}
function photoGrid(photos) {
  if (!photos || !photos.length) return '<div class="muted">No photos attached.</div>';
  return `<div style="display:flex;flex-wrap:wrap;gap:8px">${photos.map((p) => `<img class="photo-thumb" data-src="${p.dataUrl}" src="${p.dataUrl}" alt="evidence" style="width:92px;height:92px;object-fit:cover;border-radius:8px;border:1px solid var(--border,#e4e7ec);cursor:zoom-in">`).join('')}</div>`;
}
function bindPhotoLightbox() {
  document.querySelectorAll('.photo-thumb').forEach((el) => el.onclick = () => {
    const root = document.getElementById('modal-root');
    root.innerHTML = `<div class="modal-back" data-close-bg style="cursor:zoom-out"><img src="${el.dataset.src}" style="max-width:92vw;max-height:88vh;border-radius:10px;box-shadow:0 8px 40px rgba(0,0,0,.4)"></div>`;
    root.querySelector('[data-close-bg]').onclick = () => { root.innerHTML = ''; };
  });
}

/* ---- KPI card ---- */
function kpi(label, value, opts = {}) {
  // tone: 'bad' (red) | 'warn' (amber) | 'good' (green — a healthy zero, distinct from an
  // unset/gap zero) | '' (neutral).
  const cls = opts.tone === 'bad' ? 'bad' : opts.tone === 'warn' ? 'warn' : opts.tone === 'good' ? 'good' : '';
  const tick = opts.tone === 'good' ? '<span class="kpi-ok" aria-hidden="true">✓</span> ' : '';
  return `<div class="card card-pad kpi ${cls}" ${opts.link ? `data-go="${opts.link}"` : ''}>
    <div class="accent-bar"></div>
    <div class="kpi-label">${esc(label)}</div>
    <div class="kpi-value">${tick}${value == null ? '—' : esc(value)}${opts.suffix ? `<small>${esc(opts.suffix)}</small>` : ''}</div>
    <div class="kpi-foot">${opts.foot || ''}</div>
  </div>`;
}

/* ---- stacked-bar trend ---- */
function trendChart(months, series) {
  const max = Math.max(1, ...months.map(m => series.reduce((s, k) => s + (m[k.key] || 0), 0)));
  const cols = months.map(m => {
    const segs = series.map(k => { const v = m[k.key] || 0; const h = (v / max) * 100; return v ? `<div class="seg" style="height:${h}%;background:${k.color}" data-tip="${esc(m.label)} — ${esc(k.label)}: ${v}" title="${esc(k.label)}: ${v}"></div>` : ''; }).join('');
    const total = series.reduce((s, k) => s + (m[k.key] || 0), 0);
    return `<div class="col"><div style="font-size:11px;font-weight:700;color:var(--ink-2)">${total || ''}</div><div class="stack" data-tip="${esc(m.label)}: ${total} total">${segs}</div><div class="lbl">${esc(m.label)}</div></div>`;
  }).join('');
  const legend = series.map(k => `<span><i style="background:${k.color}"></i>${esc(k.label)}</span>`).join('');
  return `<div class="spark" role="img" aria-label="${esc(series.map(s => s.label).join(', '))} by month">${cols}</div><div class="legend">${legend}</div>`;
}

/* ---- page header ---- */
// NOTE: `sub` is rendered as TRUSTED HTML so page subtitles can carry help() tooltips and
// entities. Callers MUST escape any user/tenant-supplied value interpolated into `sub`
// (e.g. esc(State.tenant.name)). `title` is always escaped.
function pageHead(title, sub, actions) {
  return `<div class="page-head"><div><h2>${esc(title)}</h2>${sub ? `<div class="sub">${sub}</div>` : ''}</div><div class="page-actions">${actions || ''}</div></div>`;
}
function backLink(href, label) { return `<a data-go="${href}" style="font-size:13px;display:inline-flex;gap:5px;margin-bottom:12px;cursor:pointer">← ${esc(label)}</a>`; }
/* Shared record header — one consistent detail-page head for every record type.
 * o: { back:{href,label}, ref, title, badges:[htmlString], meta:[{label,value}], actions:htmlString }
 * `badges` and `actions` are trusted HTML (pass badge()/button markup); ref/title/meta are escaped. */
function recordHeader(o) {
  o = o || {};
  const badges = (o.badges || []).filter(Boolean).join(' ');
  const meta = (o.meta || []).filter((m) => m && m.value != null && m.value !== '');
  return `${o.back ? backLink(o.back.href, o.back.label) : ''}
    <div class="detail-head">
      <div>
        ${o.ref ? `<div class="ref" style="font-size:12px">${esc(o.ref)}</div>` : ''}
        <h2>${esc(o.title || '')}</h2>
        ${badges ? `<div style="margin-top:4px">${badges}</div>` : ''}
        ${meta.length ? `<div class="rec-meta">${meta.map((m) => `<span><b>${esc(m.label)}</b> ${esc(String(m.value))}</span>`).join('')}</div>` : ''}
      </div>
      <div class="page-actions">${o.actions || ''}</div>
    </div>`;
}

/* ---- Side drawer — peek a record without leaving the current page ----
 * openDrawer({ title, subtitle, html, actions }) slides a panel in from the right.
 * Closes on Esc, backdrop click, or the × button, and restores focus. */
function closeDrawer() {
  const back = document.getElementById('drawer-back');
  if (!back) return;
  if (back.__onKey) document.removeEventListener('keydown', back.__onKey, true);
  back.classList.remove('open');
  const rf = back.__returnFocus;
  setTimeout(() => { back.remove(); if (rf && rf.focus) { try { rf.focus(); } catch (_) {} } }, 180);
}
function openDrawer(o) {
  o = o || {};
  closeDrawer();
  const back = document.createElement('div');
  back.id = 'drawer-back'; back.className = 'drawer-back';
  back.__returnFocus = document.activeElement;
  back.innerHTML = `<aside class="drawer" role="dialog" aria-modal="true" aria-label="${esc(o.title || 'Record preview')}">
      <div class="drawer-h"><div><div class="drawer-title">${esc(o.title || '')}</div>${o.subtitle ? `<div class="drawer-sub">${esc(o.subtitle)}</div>` : ''}</div>
        <button class="x" data-dclose aria-label="Close preview">&times;</button></div>
      <div class="drawer-b">${o.html || ''}</div>
      ${o.actions ? `<div class="drawer-f">${o.actions}</div>` : ''}</aside>`;
  document.body.appendChild(back);
  const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); closeDrawer(); } };
  back.__onKey = onKey; document.addEventListener('keydown', onKey, true);
  back.addEventListener('click', (e) => { if (e.target === back) closeDrawer(); });
  back.querySelectorAll('[data-dclose]').forEach((b) => b.onclick = closeDrawer);
  if (typeof bindGo === 'function') bindGo(back);
  requestAnimationFrame(() => back.classList.add('open'));
  return back;
}
/* Peek a flagship record (incident | hazard | capa) inline. Fetches the same
 * endpoint the full page uses, renders a compact summary + "Open full record". */
const PEEK_KINDS = {
  incident: { url: (id) => '/api/incidents/' + id, route: (id) => '#/incidents/' + id, pick: (r) => r.incident,
    render: (i) => [['Status', titleCase(i.status)], ['Severity', titleCase(i.severity)], ['Type', titleCase(i.type)], ['Reported by', i.reportedByName], ['Occurred', typeof fmtDateTime === 'function' ? fmtDateTime(i.occurredAt) : i.occurredAt]] },
  hazard: { url: (id) => '/api/hazards/' + id, route: (id) => '#/hazards/' + id, pick: (r) => r.hazard,
    render: (h) => [['Status', titleCase(h.status)], ['Risk', titleCase(h.riskLevel)], ['Category', titleCase(h.category)], ['Reported by', h.reportedByName]] },
  capa: { url: (id) => '/api/capas/' + id, route: (id) => '#/capa/' + id, pick: (r) => r.capa,
    render: (c) => [['Status', titleCase(c.status)], ['Priority', titleCase(c.priority)], ['Owner', c.ownerName], ['Due', typeof fmtDate === 'function' ? fmtDate(c.dueAt) : c.dueAt]] },
  risk: { url: (id) => '/api/risks/' + id, route: (id) => '#/risk/' + id, pick: (r) => r.risk,
    render: (x) => [['Status', titleCase(x.status)], ['Max residual', (x.maxResidualBand ? titleCase(x.maxResidualBand) : '') + (x.maxResidualScore ? ' (' + x.maxResidualScore + ')' : '')], ['Assessor', x.assessorName], ['Review due', typeof fmtDate === 'function' ? fmtDate(x.reviewDueAt) : x.reviewDueAt]] },
  audit: { url: (id) => '/api/audits/' + id, route: (id) => '#/audits/' + id, pick: (r) => r.audit,
    render: (x) => [['Status', titleCase(x.status)], ['Standard', x.standard], ['Type', titleCase(x.type)], ['Score', x.score != null ? x.score + '%' : '—']] },
  permit: { url: (id) => '/api/permits/' + id, route: (id) => '#/permits/' + id, pick: (r) => r.permit,
    render: (x) => [['Status', titleCase(x.status)], ['Type', titleCase(x.type)], ['Contractor', x.contractor], ['Valid to', typeof fmtDateTime === 'function' ? fmtDateTime(x.validTo) : x.validTo]] },
};
/* Bulk-action factory for table({ bulk:[...] }). Applies one allow-listed field to
 * every selected row via POST /api/bulk/update (validated + audit-logged server-side),
 * then calls refresh() to re-render. options: array for a <select>. */
function bulkSetField(collection, field, label, options, refresh) {
  return { label, icon: '✎', run: (rows) => {
    const ids = (rows || []).map((r) => r && r.id).filter(Boolean);
    if (!ids.length) return;
    openModal({ title: `${label} — ${ids.length} record${ids.length > 1 ? 's' : ''}`, submitLabel: 'Apply to all', fields: [
      { name: 'val', label, type: 'select', options },
      { type: 'static', label: '', value: `<div class="muted" style="font-size:12px">This change is applied to all ${ids.length} selected records and recorded on each record's audit trail.</div>` },
    ], onSubmit: async (d) => {
      const r = await api.post('/api/bulk/update', { collection, ids, patch: { [field]: d.val } });
      toast(`Updated ${r.updated}${r.skipped ? ` · ${r.skipped} skipped` : ''}`);
      if (typeof refresh === 'function') refresh();
    } });
  } };
}
/* Deterministic natural-language → filter params (no LLM). Maps common phrases to the
 * same URL params applyHashFilter already understands, so "open major incidents" filters
 * the register. Pure + testable. */
function parseNlFilter(q, domain) {
  const s = String(q || '').toLowerCase();
  const has = (...w) => w.some((x) => s.includes(x));
  const p = {};
  domain = domain || 'incidents';
  // Status vocabulary (shared across registers; a value not valid for the domain simply matches nothing).
  if (has('investigat')) p.status = 'investigating';
  else if (has('triage')) p.status = 'triaged';
  else if (has('action pending', 'pending action', 'awaiting action')) p.status = 'action_pending';
  else if (has('scheduled')) p.status = 'scheduled';
  else if (has('submitted')) p.status = 'submitted';
  else if (has('under review', 'in review')) p.status = 'under_review';
  else if (has('approved')) p.status = 'approved';
  else if (has('implemented')) p.status = 'implemented';
  else if (has('verification', 'verifying')) p.status = 'verification';
  else if (has('verified')) p.status = 'verified';
  else if (has('rejected')) p.status = 'rejected';
  else if (has('actioned')) p.status = 'actioned';
  else if (has('in progress', 'in-progress')) p.status = 'in_progress';
  else if (has('cancelled', 'canceled')) p.status = 'cancelled';
  else if (has('draft')) p.status = 'draft';
  else if (has('completed', 'complete')) p.status = 'completed';
  else if (has('closed', 'resolved')) p.status = 'closed';
  else if (has('open', 'not closed', 'outstanding')) p.status = 'open';
  // Scale words → severity (incidents) / risk (hazards) / priority (CAPA); ignored for other registers.
  const scale = has('near miss', 'near-miss', 'nearmiss') ? 'near_miss' : has('fatal') ? 'fatal'
    : has('critical') ? 'critical' : has('major') ? 'major' : has('serious') ? 'serious'
    : has('moderate') ? 'moderate' : has('high') ? 'high' : has('medium') ? 'medium'
    : has('minor') ? 'minor' : has('low') ? 'low' : null;
  if (scale) {
    if (domain === 'hazards') { if (['low', 'medium', 'high'].includes(scale)) p.risk = scale; }
    else if (domain === 'capa') { if (['low', 'medium', 'high', 'critical'].includes(scale)) p.priority = scale; }
    else if (domain === 'incidents') { if (['near_miss', 'minor', 'moderate', 'serious', 'major', 'fatal'].includes(scale)) p.severity = scale; }
  }
  if (domain === 'capa' && has('overdue', 'past due')) p.overdue = 'true';
  if (domain === 'incidents' && has('reportable')) p.reportable = 'true';
  return p;
}
function nlFilterBar(placeholder) {
  return `<div style="margin:0 0 12px"><input id="nlf" placeholder="${esc(placeholder || 'Filter in plain English — e.g. “open major incidents”')}" aria-label="Filter in plain English" style="width:100%;max-width:440px;padding:8px 12px;border:1px solid var(--line,#e4e7ec);border-radius:8px;font-size:13px;background:var(--card,#fff)"></div>`;
}
function bindNlFilter(routeBase, domain) {
  const el = document.getElementById('nlf'); if (!el) return;
  el.onkeydown = (e) => {
    if (e.key !== 'Enter') return;
    const p = parseNlFilter(el.value, domain);
    const qs = Object.keys(p).map((k) => k + '=' + encodeURIComponent(p[k])).join('&');
    if (typeof toast === 'function' && !qs && el.value.trim()) toast('No filters recognised in that phrase', 'err');
    location.hash = routeBase + (qs ? '?' + qs : '');
  };
}
/* Bulk assign-to-person action. Fetches the tenant's users, lets the operator pick one,
 * and applies buildPatch(user) (e.g. {assigneeId} or {ownerId, ownerName}) to every row. */
function bulkAssign(collection, label, buildPatch, refresh) {
  return { label, icon: '👤', run: async (rows) => {
    const ids = (rows || []).map((r) => r && r.id).filter(Boolean);
    if (!ids.length) return;
    let users = [];
    try { users = (await api.get('/api/users')).users || []; } catch (_) { if (typeof toast === 'function') toast('Cannot load users', 'err'); return; }
    if (!users.length) { if (typeof toast === 'function') toast('No users available to assign', 'err'); return; }
    openModal({ title: `${label} — ${ids.length} record${ids.length > 1 ? 's' : ''}`, submitLabel: 'Assign', fields: [
      { name: 'uid', label, type: 'select', options: users.map((u) => ({ value: u.id, label: u.name + (u.role ? ' · ' + titleCase(u.role) : '') })) },
      { type: 'static', label: '', value: `<div class="muted" style="font-size:12px">Applied to all ${ids.length} selected records and recorded on each record's audit trail.</div>` },
    ], onSubmit: async (d) => {
      const u = users.find((x) => x.id === d.uid) || { id: d.uid, name: '' };
      const r = await api.post('/api/bulk/update', { collection, ids, patch: buildPatch(u) });
      if (typeof toast === 'function') toast(`Assigned ${r.updated}${r.skipped ? ` · ${r.skipped} skipped` : ''}`);
      if (typeof refresh === 'function') refresh();
    } });
  } };
}
function isPeekable(kind) { return !!PEEK_KINDS[kind]; }
/* Render a small ⤢ peek button for an entity if its kind is previewable, else ''. */
function peekBtn(kind, id, ref) { return isPeekable(kind) && id ? `<button class="peek-btn" data-peek="${kind}:${id}" title="Quick preview" aria-label="Preview${ref ? ' ' + esc(ref) : ''}">⤢</button>` : ''; }
async function peekRecord(kind, id) {
  const spec = PEEK_KINDS[kind];
  if (!spec) { if (typeof go === 'function') go('#/' + kind + '/' + id); return; }
  openDrawer({ title: 'Loading…', html: '<div class="muted" style="padding:20px 0">Fetching record…</div>' });
  try {
    const rec = spec.pick(await api.get(spec.url(id)));
    const rows = spec.render(rec).filter((r) => r[1] != null && r[1] !== '')
      .map((r) => `<div class="peek-row"><span class="peek-k">${esc(r[0])}</span><span class="peek-v">${esc(String(r[1]))}</span></div>`).join('');
    const body = `<div class="peek-ref">${esc(rec.ref || '')}</div><h3 class="peek-title">${esc(rec.title || '')}</h3>
      ${rec.description ? `<p class="muted" style="font-size:13px;margin:6px 0 14px">${esc(String(rec.description).slice(0, 260))}${String(rec.description).length > 260 ? '…' : ''}</p>` : ''}
      <div class="peek-rows">${rows}</div>`;
    openDrawer({ title: titleCase(kind) + ' preview', html: body,
      actions: `<button class="btn btn-ghost btn-sm" data-dclose>Close</button><a class="btn btn-primary btn-sm" data-go="${spec.route(id)}" data-dclose>Open full record →</a>` });
  } catch (e) {
    openDrawer({ title: 'Preview unavailable', html: `<div class="muted" style="padding:16px 0">Could not load this record.</div>`,
      actions: `<a class="btn btn-primary btn-sm" data-go="${spec.route(id)}" data-dclose>Open full record →</a>` });
  }
}
/* One-time delegated handler: any element with data-peek="kind:id" opens a drawer. */
if (typeof document !== 'undefined' && !window.__peekBound) {
  window.__peekBound = true;
  // Capture phase so we intercept before a wrapping [data-go] row's bubble handler.
  document.addEventListener('click', (e) => {
    const el = e.target.closest && e.target.closest('[data-peek]');
    if (!el) return;
    e.preventDefault(); e.stopPropagation();
    const [kind, id] = String(el.getAttribute('data-peek')).split(':');
    if (kind && id) peekRecord(kind, id);
  }, true);
}
/* Reusable record timeline — feed it activity events (from /api/activity?entityId=…),
 * renders one chronological lifecycle (oldest → newest). Any detail page can drop this in. */
function entityTimeline(events) {
  if (!events || !events.length) return '<div class="muted" style="font-size:12.5px">No timeline events yet.</div>';
  const rt = (typeof relTime === 'function') ? relTime : (x) => esc(String(x || '').slice(0, 16).replace('T', ' '));
  const rows = events.slice().reverse().map((e) => {
    const col = e.severity === 'warning' ? 'var(--warning,#f59e0b)' : e.severity === 'critical' ? 'var(--danger,#ef4444)' : 'var(--success,#0e9c74)';
    return `<li style="position:relative;padding:0 0 12px 16px">
      <span style="position:absolute;left:-5px;top:3px;width:9px;height:9px;border-radius:50%;background:${col};box-shadow:0 0 0 2px var(--card,#fff)"></span>
      <div style="font-size:12.5px;font-weight:600">${esc(e.title || e.type || 'Event')}</div>
      ${e.detail ? `<div class="muted" style="font-size:11.5px">${esc(e.detail)}</div>` : ''}
      <div class="muted" style="font-size:11px">${esc(e.actorName || '')}${e.actorName ? ' · ' : ''}${rt(e.at)}</div></li>`;
  }).join('');
  return `<ul style="list-style:none;margin:0;padding:0 0 0 5px;border-left:2px solid var(--line,#eef1f0)">${rows}</ul>`;
}
/* ---- contextual help dot — an accessible "?" with an explanatory tooltip ---- */
function infoDot(text) { return `<span class="info-dot" tabindex="0" role="button" aria-label="Help: ${esc(text)}" data-tip="${esc(text)}" title="${esc(text)}">?</span>`; }

/* ---- tabbed record detail ---- tabs:[{id,label,badge?}]; panels are
 * <div class="rec-panel" data-panel="id">. Call bindRecordTabs() after render. */
function recordTabs(tabs, activeId) {
  const active = activeId || (tabs[0] || {}).id;
  return `<div class="rec-tabs" role="tablist">${tabs.map(t => `<button class="rec-tab ${t.id === active ? 'on' : ''}" role="tab" aria-selected="${t.id === active}" data-tab="${esc(t.id)}">${esc(t.label)}${t.badge != null ? ` <span class="rt-badge">${esc(t.badge)}</span>` : ''}</button>`).join('')}</div>`;
}
function bindRecordTabs(scope) {
  const root = scope || document;
  const tabs = [...root.querySelectorAll('.rec-tab')];
  const panels = [...root.querySelectorAll('.rec-panel')];
  if (!tabs.length) return;
  const show = (id) => { tabs.forEach(t => { const on = t.dataset.tab === id; t.classList.toggle('on', on); t.setAttribute('aria-selected', on); }); panels.forEach(p => { p.style.display = p.dataset.panel === id ? '' : 'none'; }); };
  tabs.forEach(t => t.onclick = () => show(t.dataset.tab));
  const first = tabs.find(t => t.classList.contains('on')) || tabs[0];
  show(first.dataset.tab);
}

/* Reusable assurance block — evidence, digital signatures and field-level audit
 * trail for any module record. Call mountAssurance('incidents', id, '#sel'). */
async function mountAssurance(module, recordId, sel) {
  const host = document.querySelector(sel); if (!host) return;
  const canReview = State.can('audit:update') || State.can('*') || ['owner', 'admin', 'ehs_manager', 'supervisor'].includes((State.user || {}).role);
  let ev, sigs, trail;
  try {
    [ev, sigs, trail] = await Promise.all([
      api.get('/api/evidence?module=' + module + '&recordId=' + recordId),
      api.get('/api/signatures/' + module + '/' + recordId),
      api.get('/api/audit-trail/' + module + '/' + recordId),
    ]);
  } catch (_) { return; }
  const rs = { pending: 'b-amber', accepted: 'b-green', rejected: 'b-red' };
  const isImg = (u) => /^data:image\//.test(u || '');
  host.innerHTML = `
    <div class="card card-pad"><div class="card-h"><h3>📎 Evidence</h3><button class="btn btn-ghost btn-sm" id="ev-add">+ Attach</button></div>
      ${ev.evidence.length ? ev.evidence.map(e => `<div class="linkrow" style="cursor:default">${isImg(e.dataRef) ? `<img class="photo-thumb" data-src="${e.dataRef}" src="${e.dataRef}" style="width:40px;height:40px;object-fit:cover;border-radius:6px;border:1px solid var(--line);cursor:zoom-in">` : '<span style="font-size:18px">📄</span>'}<div style="flex:1"><b>${esc(e.title || e.evidenceType)}</b><div class="muted" style="font-size:11px">${esc(e.evidenceType)} · ${esc(e.uploadedBy)}${e.fileUrl ? ` · <a href="${esc(e.fileUrl)}" target="_blank" rel="noopener">link</a>` : ''}</div></div>${badge(rs[e.reviewStatus] || 'b-grey', e.reviewStatus)}${(canReview && e.reviewStatus === 'pending') ? `<button class="btn btn-ghost btn-sm" data-evok="${e.id}">Accept</button><button class="btn btn-ghost btn-sm" data-evno="${e.id}" style="color:var(--red-500)">Reject</button>` : ''}</div>`).join('') : '<div class="muted">No evidence attached.</div>'}
    </div>
    <div class="card card-pad" style="margin-top:12px"><div class="card-h"><h3>✍ Signatures</h3><button class="btn btn-ghost btn-sm" id="sig-add">Sign</button></div>
      ${sigs.signatures.length ? sigs.signatures.map(s => `<div class="linkrow" style="cursor:default"><div style="flex:1"><b>${esc(s.signedBy)}</b> · ${esc(s.action)}<div class="muted" style="font-size:11px">${esc(s.meaning)} — ${new Date(s.signedAt).toLocaleString()}</div></div>${/reauth/.test(s.authMethod || '') ? '<span class="badge b-green" title="Identity re-verified at the moment of signing">re-authenticated</span>' : '<span class="badge b-grey" title="Recorded from an authenticated session (no re-auth ceremony)">session</span>'}<span class="badge b-grey" title="${esc(s.signatureHash)}">🔒</span></div>`).join('') : '<div class="muted">No signatures yet.</div>'}
    </div>
    <details class="card card-pad" style="margin-top:12px"><summary style="cursor:pointer"><b>🧾 Audit trail</b> <span class="muted">(${trail.trail.length} change${trail.trail.length === 1 ? '' : 's'}) </span>${trail.integrity ? badge(trail.integrity.intact ? 'b-green' : 'b-red', trail.integrity.intact ? 'chain intact' : 'TAMPERED') : ''}</summary>
      <div style="margin-top:8px">${trail.trail.length ? trail.trail.slice().reverse().map(t => `<div style="padding:4px 0;border-bottom:1px solid var(--line);font-size:12px"><b>${esc(t.fieldPath || 'field')}</b>${t.oldValueText !== undefined ? ` <span class="muted">changed</span> <s style="color:var(--ink-3);word-break:break-all">${esc(String(t.oldValueText === '' ? '(empty)' : t.oldValueText))}</s> → <b style="word-break:break-all">${esc(String(t.newValueText === '' ? '(empty)' : t.newValueText))}</b>` : ` <span class="muted">${esc(t.summary || 'changed')}</span>`}<span class="muted" style="display:block;font-size:11px">${esc(t.changedBy)} · ${new Date(t.changedAt).toLocaleString()}${t.changeReason ? ' · ' + esc(t.changeReason) : ''}</span></div>`).join('') : '<div class="muted">No changes recorded.</div>'}</div>
    </details>`;
  if (typeof bindPhotoLightbox === 'function') bindPhotoLightbox();
  const re = () => mountAssurance(module, recordId, sel);
  host.querySelector('#ev-add').onclick = () => { window.__evDataRef = ''; openModal({ title: 'Attach evidence', size: 'lg', submitLabel: 'Attach', fields: [
    formGroupFields({ name: 'evidenceType', label: 'Type', type: 'select', options: ['photo', 'document', 'certificate', 'measurement', 'witness', 'url', 'reading'] }, { name: 'title', label: 'Title', required: true }),
    { name: 'description', label: 'Description', type: 'textarea', rows: 2 },
    { name: 'fileUrl', label: 'Link (optional, https)' },
    { type: 'static', label: 'Photo (optional)', value: '<div style="display:flex;align-items:center;gap:10px"><img id="ev-img" style="display:none;width:54px;height:54px;object-fit:cover;border-radius:8px;border:1px solid var(--line)"><button type="button" class="btn btn-ghost btn-sm" id="ev-cap">📷 Capture</button></div>' },
  ], onRender: () => { const b = document.getElementById('ev-cap'); if (b) b.onclick = async () => { const u = await pickImage(); if (u) { window.__evDataRef = u; const im = document.getElementById('ev-img'); im.src = u; im.style.display = 'block'; } }; },
    onSubmit: async d => { const geo = await getGeo().catch(() => null); await api.post('/api/evidence', Object.assign(d, { module, recordId, dataRef: window.__evDataRef || '', geo })); window.__evDataRef = ''; toast('Evidence attached'); re(); } }); };
  host.querySelector('#sig-add').onclick = () => openModal({ title: 'Sign record', submitLabel: 'Sign', fields: [
    { name: 'action', label: 'Action', type: 'select', options: ['approve', 'review', 'verify', 'authorise', 'acknowledge', 'close'] },
    { name: 'meaning', label: 'Meaning of signature', required: true, placeholder: 'e.g. I approve this investigation and its actions' },
    // P0-8 (21 CFR Part 11): an e-signature requires re-authentication at the moment of signing.
    formGroupFields(
      { name: 'signPassword', label: 'Your password (required to sign)', type: 'password', required: true, placeholder: 'confirm your identity' },
      { name: 'signCode', label: 'Authenticator code', placeholder: 'only if MFA is on' }),
    { type: 'static', label: '', value: '<div class="muted" style="font-size:12px">Your electronic signature is the legally binding equivalent of your handwritten signature. Re-entering your password verifies it is you, right now — the signature records who, when, what it means, and how you were authenticated.</div>' },
  ], onSubmit: async d => { const geo = await getGeo().catch(() => null); await api.post('/api/signatures', Object.assign(d, { module, recordId, geo })); toast('Signed'); re(); } });
  host.querySelectorAll('[data-evok]').forEach(b => b.onclick = async () => { await api.patch('/api/evidence/' + b.dataset.evok + '/review', { reviewStatus: 'accepted' }); toast('Accepted'); re(); });
  host.querySelectorAll('[data-evno]').forEach(b => b.onclick = async () => { await api.patch('/api/evidence/' + b.dataset.evno + '/review', { reviewStatus: 'rejected', rejectionReason: 'rejected on review' }); toast('Rejected'); re(); });
}
function progressBar(pct, tone) { return `<div class="bar ${tone || ''}"><i style="width:${Math.max(0, Math.min(100, pct))}%"></i></div>`; }

/* Northstar Assist — subtle, opt-in "explore expert help" affordance. Drop
 * assistCTA(service, label) into any view, then call bindAssist() after render. */
function assistCTA(service, label, ctxModule, ctxId) {
  return `<button class="btn btn-ghost btn-sm" data-assist-svc="${esc(service)}" data-assist-label="${esc(label || '')}" data-assist-mod="${esc(ctxModule || '')}" data-assist-id="${esc(ctxId || '')}" style="font-size:12px;color:var(--green-600)">💬 Explore expert help</button>`;
}
function bindAssist() {
  document.querySelectorAll('[data-assist-svc]').forEach((b) => { if (b.__wired) return; b.__wired = true; b.onclick = () => openAssistRequest(b.dataset.assistSvc, b.dataset.assistLabel, b.dataset.assistMod, b.dataset.assistId); });
}
/* A subtle inline "expert help" strip — one line of context + one or more
 * service chips. services: [{ key, label }]. Valid keys: credit_sourcing,
 * audit_support, training_delivery, epr_compliance, govt_liaison,
 * documentation, certification, sustainability_advisory. bindAssist() runs
 * automatically from render(). */
function assistStrip(contextModule, note, services, ctxId) {
  const chips = (services || []).map((s) => `<button class="btn btn-ghost btn-sm" data-assist-svc="${esc(s.key)}" data-assist-label="${esc(s.label || note || '')}" data-assist-mod="${esc(contextModule || '')}" data-assist-id="${esc(ctxId || '')}" style="font-size:11.5px;color:var(--green-600);border:1px solid var(--line)">💬 ${esc(s.label || 'Explore expert help')}</button>`).join(' ');
  return `<div class="assist-strip" style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;background:var(--green-50,#f0faf6);border:1px dashed var(--green-100,#cdeee1);border-radius:10px;padding:9px 12px;margin:14px 0;font-size:12px">
    <span style="font-size:14px">💡</span><span class="muted" style="flex:1;min-width:160px">${esc(note || 'Northstar can help with this — expert support is optional.')}</span>${chips}</div>`;
}
function openAssistRequest(service, label, ctxModule, ctxId) {
  openModal({ title: 'Request expert help', submitLabel: 'Send request', fields: [
    { type: 'static', label: '', value: '<div class="muted" style="font-size:12px">Optional Northstar Assist service. Sending this simply asks our team to reach out — no obligation, and nothing is purchased or filed by the software.</div>' },
    { name: 'subject', label: 'What do you need help with?', value: label || '' },
    { name: 'notes', label: 'Details (optional)', type: 'textarea', rows: 3 },
  ], onSubmit: async (d) => { await api.post('/api/assist/requests', { service, subject: d.subject, notes: d.notes, contextModule: ctxModule || '', contextRecordId: ctxId || null, contextLabel: label || '' }); toast('Request sent — the Northstar team will be in touch'); } });
}
